import { InternalMessage } from '@/lib/api/internal-chat/internal-chat-api'
import { HiSparkles } from 'react-icons/hi2'
import { MdAttachFile, MdDownload, MdReply } from 'react-icons/md'
import ReactMarkdown from 'react-markdown'
import AudioPlayer from './AudioPlayer'
import MessageStatus from './MessageStatus'
import YouTubeEmbed from './YouTubeEmbed'

interface MessageBubbleProps {
    message: InternalMessage
    isOwn: boolean
    onReply: (message: InternalMessage) => void
}

type FileType = 'image' | 'audio' | 'video' | 'file'

const getFileType = (url: string): FileType => {
    const ext = url.split('.').pop()?.toLowerCase() || ''
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext))
        return 'image'
    if (['mp3', 'wav', 'ogg', 'webm', 'm4a', 'aac'].includes(ext))
        return 'audio'
    if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return 'video'
    return 'file'
}

const getYouTubeId = (url: string): string | null => {
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    )
    return match ? match[1] : null
}

const getFilename = (url: string): string => {
    const parts = url.split('/')
    return parts[parts.length - 1] || 'arquivo'
}

export default function MessageBubble({
    message,
    isOwn,
    onReply,
}: MessageBubbleProps) {
    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const senderName = message.isAiMessage
        ? 'Assistente IA'
        : message.sender?.name || 'Usuário'

    const renderAttachment = (url: string, index: number) => {
        const type = getFileType(url)

        switch (type) {
            case 'image':
                return (
                    <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-2"
                    >
                        <img
                            src={url}
                            alt="Imagem"
                            className="max-w-full max-h-60 rounded cursor-pointer hover:opacity-90"
                        />
                    </a>
                )
            case 'audio':
                return (
                    <div key={index} className="mt-2">
                        <AudioPlayer src={url} />
                    </div>
                )
            case 'video':
                return (
                    <video
                        key={index}
                        controls
                        className="max-w-full max-h-60 rounded mt-2"
                    >
                        <source src={url} />
                        Seu navegador nao suporta video.
                    </video>
                )
            default:
                return (
                    <a
                        key={index}
                        href={url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 p-2 rounded mt-2 ${
                            isOwn
                                ? 'bg-primary-focus/30 hover:bg-primary-focus/50'
                                : 'bg-base-300 hover:bg-base-300/80'
                        }`}
                    >
                        <MdAttachFile className="text-lg flex-shrink-0" />
                        <span className="text-sm truncate flex-1">
                            {getFilename(url)}
                        </span>
                        <MdDownload className="text-lg flex-shrink-0" />
                    </a>
                )
        }
    }

    const renderContent = (text: string) => {
        if (!text) return null

        const urlRegex = /(https?:\/\/[^\s]+)/g
        const parts = text.split(urlRegex)

        return parts.map((part, index) => {
            if (urlRegex.test(part)) {
                urlRegex.lastIndex = 0

                const youtubeId = getYouTubeId(part)
                if (youtubeId) {
                    return <YouTubeEmbed key={index} videoId={youtubeId} />
                }

                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`underline ${
                            isOwn
                                ? 'text-primary-content hover:text-primary-content/80'
                                : 'text-secondary hover:text-secondary/80'
                        }`}
                    >
                        {part}
                    </a>
                )
            }
            return <span key={index}>{part}</span>
        })
    }

    return (
        <div
            className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}
        >
            <div
                className={`max-w-[80%] md:max-w-[70%] ${
                    isOwn
                        ? 'bg-secondary text-primary-content'
                        : message.isAiMessage
                          ? 'bg-accent/10 border border-accent/30'
                          : 'bg-base-200'
                } rounded-lg px-3 py-2 relative`}
            >
                {/* Reply button */}
                <button
                    type="button"
                    className={`absolute top-1 ${isOwn ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs btn-circle`}
                    onClick={() => onReply(message)}
                    title="Responder"
                >
                    <MdReply className="text-base" />
                </button>

                {/* Sender name (for received messages) */}
                {!isOwn && (
                    <div
                        className={`text-xs font-medium mb-1 flex items-center gap-1 ${
                            message.isAiMessage
                                ? 'text-secondary'
                                : 'text-secondary'
                        }`}
                    >
                        {message.isAiMessage && <HiSparkles />}
                        {senderName}
                    </div>
                )}

                {/* Reply preview */}
                {message.replyTo && (
                    <div
                        className={`text-xs mb-2 p-2 rounded border-l-2 ${
                            isOwn
                                ? 'bg-primary-focus/30 border-primary-content/50'
                                : 'bg-base-300/50 border-base-content/30'
                        }`}
                    >
                        <div className="font-medium flex items-center gap-1">
                            {message.replyTo.isAiMessage && (
                                <HiSparkles className="text-secondary" />
                            )}
                            {message.replyTo.isAiMessage
                                ? 'Assistente IA'
                                : message.replyTo.senderName || 'Usuário'}
                        </div>
                        <div className="opacity-80 truncate">
                            {message.replyTo.content}
                        </div>
                    </div>
                )}

                {/* Message content */}
                {message.isAiMessage ? (
                    <div className="text-sm break-words prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-inherit prose-a:text-secondary prose-a:underline">
                        <ReactMarkdown
                            components={{
                                a: ({ href, children }) => (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {message.content}
                        </ReactMarkdown>
                    </div>
                ) : (
                    <div className="text-sm whitespace-pre-wrap break-words">
                        {renderContent(message.content)}
                    </div>
                )}

                {/* Attachments */}
                {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-1">
                        {message.attachments.map((url, index) =>
                            renderAttachment(url, index)
                        )}
                    </div>
                )}

                {/* Timestamp and status */}
                <div
                    className={`text-xs mt-1 flex items-center justify-end gap-1 ${
                        isOwn
                            ? 'text-primary-content/70'
                            : 'text-base-content/50'
                    }`}
                >
                    {formatTime(message.createdAt)}
                    <MessageStatus
                        status={message.status}
                        readCount={message.readCount}
                        totalRecipients={message.totalRecipients}
                        isOwnMessage={isOwn}
                    />
                </div>
            </div>
        </div>
    )
}
